/**
 * English: Multi-mode chat test page — tabs for insight/chat/control/agent/master/device-direct + WebSocket streaming.
 * Thai: หน้าทดสอบแชทหลายโหมด — แท็บ ข้อมูลเชิงลึก/คุย/สั่ง/agent/master/คุยตรงเครื่อง + WebSocket
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EyeIcon, UserGroupIcon, ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useStations } from '../hooks/useStations';
import { useChatOrgContext } from '../hooks/useChatOrgContext';
import { useAgents } from '../hooks/useAgents';
import { useChatWS } from '../hooks/useChatWS';
import { useChat } from '../hooks/useChat';
import { useDevices } from '../hooks/useDevices';
import ChatInput, { ChatInputMode } from '../components/chat-test/ChatInput';
import ChatHistory, { UserMessage, ChatHistoryMessage } from '../components/chat-test/ChatHistory';
import SecondaryTabs, { SecondaryTab } from '../components/chat-test/ChatTabs';
import PageHeader from '../components/layout/PageHeader';
import TalkToPanel from '../components/chat-test/AgentSidebar';
import StatusBadge from '../components/shared/StatusBadge';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { AgentLiveStatus } from '../types';
import { CHAT_TEST_QUESTIONS } from '../data/chatTestQuestions';
import {
  buildChatSessionScopeKey,
  getOrCreateChatSessionId,
  resetChatSessionId,
} from '../utils/chatSessionId';
import {
  clearChatTestTranscript,
  loadChatTestTranscript,
  saveChatTestTranscript,
} from '../utils/chatTestTranscriptCache';
import { ENDPOINTS, isMasterChatOrg } from '../config/api';
import { useApi } from '../hooks/useApi';
import { useChatSync } from '../hooks/useChatSync';
import {
  isChatTestDeviceTarget,
  isChatTestManagerTarget,
  isChatTestMasterTarget,
} from '../utils/chatTestTargets';
import AgentGraphView from '../components/chat-test/AgentGraphView';
import OrgPicker from '../components/chat-test/OrgPicker';
import { useGraphStations } from '../hooks/useGraphStations';
import {
  agentStatusesToSpeakers,
  buildGraphNodeMessages,
} from '../utils/graphChatBridge';

/** แบนเนอร์เมื่อออกจากแชทหลักไปมุมมองรอง (ไม่รวมบุคลิก — มีพื้นที่แสดงผลของตัวเอง) */
const SECONDARY_VIEW_BANNER: Record<Exclude<SecondaryTab, 'personalities'>, string> = {
  discussion:
    'กำลังดูการสนทนาระหว่างผู้จัดการสถานีแบบ A2A ครับ — มุมมองนี้แยกจากแชทหลักของ Master',
  stations:
    'กำลังดูรายละเอียดข้อมูลในสถานีย่อยครับ — กลับแชทหลักเพื่อส่งข้อความได้',
  teamDiscussion:
    'กำลังดูขั้นตอนภายในกราฟ (Contribution ของอุปกรณ์ ตามด้วย Discussion แบบ A2A) ครับ — แยกจากแชทหลักของ Manager',
};

// ========= Device Personalities grid =========
const DEVICE_ICONS: Record<string, string> = {
  powermeter: '⚡', sensor: '📡', heartbeat: '💓',
  pump: '💧', adder: '🧪', mixer: '🔀', flow: '🌊',
};

function DevicePersonalitiesGrid({ devices, loading }: { devices: Record<string, unknown>; loading: boolean }) {
  if (loading) {
    return <div className="flex items-center justify-center h-32"><LoadingSpinner size="md" /></div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
      {Object.entries(devices).map(([type, data]) => {
        const d = data as Record<string, string>;
        return (
          <div key={type} className="bg-app-bg border border-app-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{DEVICE_ICONS[type] || '⚙️'}</span>
              <h3 className="text-sm font-semibold text-app-text uppercase">{type}</h3>
            </div>
            <div className="space-y-2 text-xs">
              {d.personality && <div><span className="text-app-text-dim">Personality:</span><p className="text-app-text mt-0.5">{d.personality}</p></div>}
              {d.focus && <div><span className="text-app-text-dim">Focus:</span><p className="text-app-text mt-0.5">{d.focus}</p></div>}
              {d.perspective && <div><span className="text-app-text-dim">Perspective:</span><p className="text-app-text mt-0.5">{d.perspective}</p></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ========= Main Page =========

function ChatTestLoginCard() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError(t('login.fillUsernamePassword'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await login(username.trim(), password);
      if (!result.ok) {
        setError(result.error || t('login.unauthorized'));
      }
    } catch (err) {
      setError((err as Error).message || t('login.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-app-text">{t('chat.signInTitle')}</h1>
          <p className="text-sm text-app-text-sec mt-1">{t('chat.signInSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-app-surface border border-app-border rounded-xl p-6 space-y-4 shadow-md">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-2.5 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-app-text-sec mb-1">{t('login.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.usernamePlaceholder')}
              autoFocus
              className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2.5 text-sm text-app-text placeholder:text-app-text-dim focus:ring-1 focus:ring-app-accent outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-app-text-sec mb-1">{t('login.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2.5 text-sm text-app-text placeholder:text-app-text-dim focus:ring-1 focus:ring-app-accent outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-app-accent hover:bg-app-accent/80 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <LoadingSpinner size="sm" /> : t('login.signIn')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ChatTestPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canControl, userRole, token, userOrgName, userSubject, selectedOrgName, clearSelectedOrg } = useAuth();
  const { orgId: chatOrgId } = useChatOrgContext();
  const { stations, activeStationId, setActiveStationId, loading: stationsLoading } = useStations();
  const { graphStations, loading: graphStationsLoading, error: graphStationsError } =
    useGraphStations(stations, stationsLoading);
  // Unified chat target: 'master' | 'manager' | device agent id
  const [chatTarget, setChatTarget] = useState<string>('manager');

  const { agents, refresh: refreshAgents } = useAgents(activeStationId);
  const { devices, loading: devicesLoading } = useDevices();

  // Per-target Redis conv:{id}: each device needs its own conversation_id; manager vs master must not share one list.
  const chatSessionScopeKey = useMemo(() => {
    const base = buildChatSessionScopeKey(userOrgName, activeStationId, userSubject);
    if (isChatTestMasterTarget(chatTarget)) return `${base}::master`;
    if (isChatTestManagerTarget(chatTarget)) return base;
    return `${base}::${chatTarget}`;
  }, [userOrgName, activeStationId, userSubject, chatTarget]);
  const [secondaryTab, setSecondaryTab] = useState<SecondaryTab | null>(null);
  const [inputMode, setInputMode] = useState<ChatInputMode>('ask');

  const wsHook = useChatWS();
  const sendWsMessage = wsHook.send;
  const api = useApi();
  const { loadChatHistory } = useChat();
  const chatSessionIdRef = useRef<string>(getOrCreateChatSessionId(chatSessionScopeKey));
  const lastSyncedChatTaskIdRef = useRef<string>('');
  const pendingStopRef = useRef(false);
  const [activeTaskId, setActiveTaskId] = useState('');
  const [isStopping, setIsStopping] = useState(false);

  // Map WS hook outputs to names used by useChatSync and the rest of the page
  const wsMessages = wsHook.messages;
  // Stable ref to wsMessages so scope-change effect can read current length without
  // adding wsMessages to its dep array (which would cause spurious scope-resets).
  const wsMessagesRef = useRef(wsMessages);
  wsMessagesRef.current = wsMessages;
  const wsStatus = wsHook.status === 'connecting' ? 'idle' : wsHook.status;
  const fullState = wsHook.fullState;
  const loading = wsHook.status === 'streaming';
  const chatStartError = wsHook.error;

  // Hydrate from session cache on first render so the UI doesn't flash empty before the async Redis fetch returns.
  const initialCacheRef = useRef<ReturnType<typeof loadChatTestTranscript> | null>(null);
  if (initialCacheRef.current === null) {
    const cached = loadChatTestTranscript(chatSessionScopeKey);
    if (cached && cached.conversationId === chatSessionIdRef.current) {
      initialCacheRef.current = cached;
    }
  }
  const [history, setHistory] = useState<ChatHistoryMessage[]>(
    () => initialCacheRef.current?.history ?? [],
  );
  const [userMessages, setUserMessages] = useState<UserMessage[]>(
    () => initialCacheRef.current?.userMessages ?? [],
  );
  const [agentStatuses, setAgentStatuses] = useState<Record<string, AgentLiveStatus>>({});

  const prevLenRef = useRef(0);
  const nextClientOrderRef = useRef(initialCacheRef.current?.nextOrder ?? 0);
  const prevStationForTargetRef = useRef<string | null>(null);
  const urlTargetAppliedRef = useRef(false);

  const [prevScopeKey, setPrevScopeKey] = useState(chatSessionScopeKey);

  // Synchronous scope change handling to prevent stale cache saves
  if (prevScopeKey !== chatSessionScopeKey) {
    setPrevScopeKey(chatSessionScopeKey);
    const newId = getOrCreateChatSessionId(chatSessionScopeKey);
    chatSessionIdRef.current = newId;

    const cached = loadChatTestTranscript(chatSessionScopeKey);
    if (cached && cached.conversationId === newId) {
      setHistory(cached.history);
      setUserMessages(cached.userMessages);
      nextClientOrderRef.current = cached.nextOrder;
    } else {
      setHistory([]);
      setUserMessages([]);
      nextClientOrderRef.current = 0;
    }

    setActiveTaskId('');
    setAgentStatuses({});
    prevLenRef.current = wsMessagesRef.current.length;
    lastSyncedChatTaskIdRef.current = '';
  }

  // Derived flags
  const canUseMasterChat = isMasterChatOrg(userOrgName);
  const isMaster = isChatTestMasterTarget(chatTarget);
  const isDevice = isChatTestDeviceTarget(chatTarget);
  const isManager = isChatTestManagerTarget(chatTarget);

  useEffect(() => {
    if (!canUseMasterChat && chatTarget === 'master') {
      setChatTarget('manager');
    }
  }, [canUseMasterChat, chatTarget]);

  // Reload agents when station changes
  useEffect(() => {
    if (stationsLoading || !activeStationId) return;
    void refreshAgents();
  }, [stationsLoading, activeStationId, refreshAgents]);

  // When chatTarget changes → clear secondary tab, reset inputMode for master
  useEffect(() => {
    setSecondaryTab(null);
    if (isMaster && inputMode === 'control') setInputMode('ask');
  }, [chatTarget, isMaster]); // eslint-disable-line react-hooks/exhaustive-deps

  // When station changes (not on first mount) → reset to manager unless Master is selected
  useEffect(() => {
    if (!activeStationId) return;
    const prev = prevStationForTargetRef.current;
    prevStationForTargetRef.current = activeStationId;
    if (prev !== null && prev !== activeStationId && !isMaster) {
      setChatTarget('manager');
    }
  }, [activeStationId, isMaster]);

  // Optional deep link: /chat-test?target=manager|master|device_… (consume once)
  useEffect(() => {
    if (urlTargetAppliedRef.current) return;
    urlTargetAppliedRef.current = true;
    const t = searchParams.get('target');
    if (t && (t === 'manager' || t.startsWith('device_') || (t === 'master' && canUseMasterChat))) {
      setChatTarget(t);
    }
    if (searchParams.has('target')) {
      const next = new URLSearchParams(searchParams);
      next.delete('target');
      setSearchParams(next, { replace: true });
    }
  }, [canUseMasterChat, searchParams, setSearchParams]);

  useEffect(() => {
    if (!canControl && inputMode === 'control') setInputMode('ask');
  }, [canControl, inputMode]);

  // Unified session: GET /api/v2/chat/history keyed by conversation_id (Postgres chat_messages).
  // Scope in sessionStorage includes manager (base), master (…::master), and each device (…::{agent_id}).
  const shouldSyncRedisTranscript =
    secondaryTab === null &&
    (inputMode === 'ask' || inputMode === 'control') &&
    ((isManager && !isMaster && !isDevice) || isMaster || isDevice);

  const { historyLoadError } = useChatSync({
    shouldSyncRedisTranscript,
    inputMode,
    secondaryTab,
    token,
    chatSessionScopeKey,
    chatSessionIdRef,
    wsStatus,
    wsMessages,
    wsHookConversationId: wsHook.conversationId,
    activeTaskId,
    loadChatHistory,
    setHistory,
    setUserMessages,
    prevLenRef,
    nextClientOrderRef,
    lastSyncedChatTaskIdRef,
  });

  // Persist main-chat transcript synchronously so route changes / remounts can hydrate when Redis returns [] or stale rows.
  // Why: a debounce loses the most-recent message when the user navigates within the debounce window.
  // Skip when both arrays are empty — that is the empty initial render after remount and would clobber the good cache
  // before useChatSync has a chance to read it.
  useEffect(() => {
    if (!token || !shouldSyncRedisTranscript) return;
    if (history.length === 0 && userMessages.length === 0) return;
    const convId = chatSessionIdRef.current;
    const scope = chatSessionScopeKey;
    saveChatTestTranscript(scope, {
      conversationId: convId,
      history,
      userMessages,
      nextOrder: nextClientOrderRef.current,
    });
    return () => {
      // Flush again on unmount/scope change in case state updated synchronously after this effect ran.
      if (history.length === 0 && userMessages.length === 0) return;
      saveChatTestTranscript(scope, {
        conversationId: convId,
        history,
        userMessages,
        nextOrder: nextClientOrderRef.current,
      });
    };
  }, [token, shouldSyncRedisTranscript, chatSessionScopeKey, history, userMessages]);

  // Live agent status from WebSocket stream
  useEffect(() => {
    if (!fullState) return;
    setAgentStatuses((prev) => {
      const next = { ...prev };
      const nextAgent = fullState.next_agent;
      const activeIds = new Set(fullState.active_agent_ids || []);

      if (nextAgent === 'employees') {
        Object.keys(next).forEach((id) => {
          if (id !== 'manager' && next[id] !== 'completed') {
            next[id] = activeIds.size === 0 || activeIds.has(id) ? 'speaking' : next[id];
          }
        });
        next.manager = 'waiting';
      } else if (nextAgent === 'FINISH') {
        Object.keys(next).forEach((id) => { next[id] = 'completed'; });
      } else if (nextAgent === 'manager') {
        next.manager = 'speaking';
      }

      // Master mode updates
      if (isMaster && fullState.messages) {
        for (const msg of fullState.messages) {
          if (msg.agent_id === 'master') {
            next.master = msg.message_type === 'master_final_result' ? 'completed' : 'speaking';
          }
          if (msg.agent_id.startsWith('manager_')) {
            next[msg.agent_id] = msg.message_type === 'manager_discussion' ? 'completed' : 'speaking';
          }
        }
      }
      return next;
    });
  }, [fullState, isMaster]);

  const agentColors = useMemo(() => {
    const colors: Record<string, string> = {};
    Object.values(agents).forEach((a) => {
      if (a.color) colors[a.id] = a.color;
    });
    colors.master = '#a855f7';
    return colors;
  }, [agents]);

  const isStreaming = wsStatus === 'streaming';
  const isBusy = loading || isStreaming;

  const stopTask = useCallback(async (taskId: string) => {
    await api.post(ENDPOINTS.chatStop(taskId), {});
  }, [api]);

  const handleStop = useCallback(async () => {
    if (isStopping) return;
    const taskIdToStop = activeTaskId;
    setIsStopping(true);
    wsHook.cancelCurrentTask();
    if (!taskIdToStop) {
      setTimeout(() => setIsStopping(false), 500);
      return;
    }
    try {
      pendingStopRef.current = false;
      await stopTask(taskIdToStop);
    } catch (err) {
      console.error('Stop failed:', err);
    } finally {
      setTimeout(() => setIsStopping(false), 500);
    }
  }, [activeTaskId, isStopping, stopTask, wsHook]);

  // Sync activeTaskId from WS hook (task_started frame sets wsHook.taskId)
  useEffect(() => {
    if (wsHook.taskId) {
      setActiveTaskId(wsHook.taskId);
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        stopTask(wsHook.taskId)
          .catch((err) => console.error('Stop failed:', err))
          .finally(() => setTimeout(() => setIsStopping(false), 500));
      }
    }
  }, [wsHook.taskId, stopTask]);

  useEffect(() => {
    if (wsStatus === 'completed' || wsStatus === 'stopped' || wsStatus === 'error' || wsStatus === 'timeout') {
      pendingStopRef.current = false;
      setIsStopping(false);
    }
  }, [wsStatus]);

  // Removed the sync effect that overwrites chatSessionIdRef.current with wsHook.conversationId.
  // The frontend always sends a valid UUID, so the backend always echoes it back.
  // Syncing it back caused a race condition where a delayed task_started frame from a previous
  // tab would overwrite the new tab's conversation_id, causing them to share transcripts.

  const handleSend = useCallback((message: string) => {
    if (inputMode === 'control' && !canControl) return;
    if (isMaster && !canUseMasterChat) return;
    if (!token) return;
    pendingStopRef.current = false;
    setActiveTaskId('');
    const clientOrder = ++nextClientOrderRef.current;
    setUserMessages((prev) => [
      ...prev,
      {
        text: message,
        time: Date.now(),
        mode: isMaster ? 'master' : isDevice ? 'employee' : inputMode,
        clientOrder,
        ...(isDevice ? { chatTarget } : {}),
      },
    ]);

    // Reset agent statuses
    const initStatuses: Record<string, AgentLiveStatus> = {};
    if (inputMode === 'ask' && !isMaster && !isDevice) {
      initStatuses['assistant'] = 'speaking';
    } else if (isMaster) {
      initStatuses.master = 'speaking';
      stations.forEach((s) => { initStatuses[`manager_${s.id}`] = 'waiting'; });
    } else {
      Object.keys(agents).forEach((id) => {
        initStatuses[id] = id === 'manager' ? 'speaking' : 'waiting';
      });
    }
    setAgentStatuses(initStatuses);

    // Determine WS mode and payload
    const wsMode = isMaster ? 'master' : isDevice ? 'employee' : inputMode === 'control' ? 'control' : 'ask';
    sendWsMessage({
      token,
      message,
      mode: wsMode as import('../hooks/useChatWS').ChatMode,
      ...(chatOrgId ? { org_id: chatOrgId, org_name: chatOrgId } : {}),
      ...(activeStationId ? { station_id: activeStationId } : {}),
      ...(chatSessionIdRef.current ? { conversation_id: chatSessionIdRef.current } : {}),
      ...(isDevice ? { agent_id: chatTarget, intent: inputMode } : {}),
    });
  }, [
    isMaster, canUseMasterChat, isDevice, inputMode, chatTarget, activeStationId,
    agents, stations, canControl, token, sendWsMessage, chatOrgId,
  ]);

  const handleClear = () => {
    clearChatTestTranscript(chatSessionScopeKey);
    setHistory([]);
    setUserMessages([]);
    setActiveTaskId('');
    setAgentStatuses({});
    prevLenRef.current = 0;
    nextClientOrderRef.current = 0;
    lastSyncedChatTaskIdRef.current = '';
    chatSessionIdRef.current = resetChatSessionId(chatSessionScopeKey);
  };

  // Context label for the chat header
  const isDirectAsk = inputMode === 'ask' && !activeStationId && !isMaster && !isDevice;
  const selectedDeviceAgent = isDevice ? agents[chatTarget] : undefined;
  const targetLabel = isMaster
    ? 'Master Agent'
    : selectedDeviceAgent
      ? `${selectedDeviceAgent.name} · ${selectedDeviceAgent.role || selectedDeviceAgent.type || 'device'}`
      : isDevice
        ? chatTarget
        : isDirectAsk
          ? 'AgentStation Assistant'
          : isManager
            ? `Manager · ${stations.find((s) => s.id === activeStationId)?.name || activeStationId}`
            : chatTarget;

  const targetColor = isMaster
    ? '#a855f7'
    : isDevice
      ? (selectedDeviceAgent?.color || '#64748b')
      : isDirectAsk
        ? '#a855f7'
        : isManager
          ? (agents.manager?.color || '#2563eb')
          : '#64748b';

  const chatPlaceholder = isMaster
    ? 'ข้อมูลเชิงลึก Master ข้ามทุกสถานี… · Insights Master across stations…'
    : isManager
      ? inputMode === 'control'
        ? 'สั่งผู้จัดการสถานีนี้… · Command this station’s manager…'
        : 'คุย & ข้อมูลเชิงลึกผู้จัดการสถานีนี้… · chat & Insights this station’s manager…'
      : inputMode === 'ask' && isDevice
        ? `คุยตรงกับ ${agents[chatTarget]?.name || 'device'}… · Chat directly with ${agents[chatTarget]?.name || 'device'}…`
        : inputMode === 'control'
          ? `สั่ง ${agents[chatTarget]?.name || 'device'}… · Command ${agents[chatTarget]?.name || 'device'}…`
          : `ข้อมูลเชิงลึก ${agents[chatTarget]?.name || 'device'}… · Insights ${agents[chatTarget]?.name || 'device'}…`;

  const randomPool = (isManager || isMaster) && inputMode === 'ask' ? CHAT_TEST_QUESTIONS : undefined;

  const agentNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(agents).forEach(([id, agent]) => {
      map[id] = agent.name;
    });
    map.master = 'Master Agent';
    stations.forEach((s) => {
      map[`manager_${s.id}`] = `Manager · ${s.name || s.id}`;
    });
    return map;
  }, [agents, stations]);

  const graphNodeMessages = useMemo(
    () => buildGraphNodeMessages(wsMessages, history),
    [wsMessages, history],
  );

  const graphActiveSpeakers = useMemo(
    () => agentStatusesToSpeakers(agentStatuses, agentNameMap),
    [agentStatuses, agentNameMap],
  );

  const graphIsProcessing = wsHook.status === 'streaming';

  const showPersonalities = secondaryTab === 'personalities';
  const unifiedManagerTranscript = isManager && !isMaster && !isDevice && secondaryTab === null;

  // Show login if not authenticated
  if (!token) {
    return <ChatTestLoginCard />;
  }

  // Require explicit org selection before chat (unified admin)
  if (!selectedOrgName.trim()) {
    return (
      <OrgPicker
        onSelected={() => {
          /* selectedOrgName updates via AuthContext — re-render unlocks chat */
        }}
      />
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] gap-3">
      {!canControl && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl text-sm text-amber-800 dark:text-amber-200 shrink-0">
          <EyeIcon className="w-4 h-4 shrink-0" />
          <span>
            โหมด <strong>ควบคุม</strong> ถูกปิดสำหรับ role{' '}
            <code className="font-mono text-xs">{userRole}</code> — ยังใช้{' '}
            <strong>ข้อมูลเชิงลึก · Insights</strong> กับ <strong>คุย · Chat</strong> ได้ตามปกติ
          </span>
        </div>
      )}
      {historyLoadError && shouldSyncRedisTranscript && (
        <div className="flex items-center gap-2 px-4 py-2 bg-app-error/10 border border-app-error/30 rounded-xl text-sm text-app-error shrink-0">
          <span>
            โหลดประวัติแชทไม่สำเร็จ (GET /api/v2/chat/history): {historyLoadError}
          </span>
        </div>
      )}
      {chatStartError && (
        <div className="flex items-center gap-2 px-4 py-2 bg-app-error/10 border border-app-error/30 rounded-xl text-sm text-app-error shrink-0">
          <span>
            เริ่มงานไม่สำเร็จ (401 = ล็อกอิน/โทเคน, 400 = station/ข้อความ, 503 = Redis/BullMQ): {chatStartError}
          </span>
        </div>
      )}
      {/* Header */}
      <PageHeader title={t('chat.pageTitle')}>
        <button
          type="button"
          onClick={() => clearSelectedOrg()}
          className="flex items-center gap-2 rounded-lg border-2 border-app-accent/50 bg-app-accent/10 px-3 py-1.5 text-left hover:bg-app-accent/20 hover:border-app-accent transition-colors shrink-0 max-w-[min(100%,18rem)]"
          title={t('chat.changeOrgHint')}
          aria-label={t('chat.changeOrg')}
        >
          <div className="min-w-0">
            <div className="text-[10px] font-medium uppercase tracking-wide text-app-accent">
              {t('chat.currentOrg')}
            </div>
            <div className="font-mono text-sm font-semibold text-app-text truncate">
              {selectedOrgName}
            </div>
          </div>
          <span className="inline-flex items-center gap-1 shrink-0 rounded-md bg-app-accent px-2 py-1 text-xs font-semibold text-white">
            <ArrowsRightLeftIcon className="w-3.5 h-3.5" aria-hidden />
            {t('chat.changeOrg')}
          </span>
        </button>
        {activeTaskId && (
          <StatusBadge
            variant={
              isStreaming ? 'info'
                : wsStatus === 'completed' ? 'success'
                  : wsStatus === 'stopped' ? 'warning'
                    : wsStatus === 'error' ? 'error'
                      : 'muted'
            }
            dot
          >
            {wsStatus === 'stopped' ? 'stopped' : wsStatus}
          </StatusBadge>
        )}
        <button
          type="button"
          onClick={handleClear}
          className="px-3 py-1.5 rounded-lg text-sm font-medium text-app-text-sec hover:bg-app-muted hover:text-app-text transition-colors border border-app-border shrink-0"
        >
          {t('chat.clear')}
        </button>
      </PageHeader>

      {/* Main area */}
      <div className="flex-1 bg-app-surface border border-app-border rounded-xl overflow-hidden flex min-h-0">
        {/* Left: Talk To panel */}
        <TalkToPanel
          chatTarget={chatTarget}
          onSelect={setChatTarget}
          showMaster={canUseMasterChat}
          agentStatuses={agentStatuses}
          agents={agents}
          stations={stations}
          activeStationId={activeStationId}
          onStationChange={setActiveStationId}
        />

        {/* Center: chat + live station graph */}
        <div className="flex-1 flex flex-col lg:flex-row min-w-0 min-h-0">
          <div className="flex-1 flex flex-col min-w-0 min-h-0">

            {/* Context bar */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-app-border bg-app-muted shrink-0">
              <div
                className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                style={{ backgroundColor: targetColor }}
              >
                {targetLabel[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-app-text">{targetLabel}</span>
                {isMaster && (
                  <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                    insight อย่างเดียว · Insights only
                  </span>
                )}
                {isDevice && (
                  <span className="ml-2 text-[10px] text-app-text-dim">Direct chat · bypasses Manager</span>
                )}
              </div>
              {isBusy && (
                <span className="text-xs text-app-text-dim flex items-center gap-1 shrink-0">
                  <LoadingSpinner size="sm" /> thinking…
                </span>
              )}
              {history.length > 0 && (
                <span className="text-[11px] text-app-text-dim tabular-nums shrink-0">
                  {history.length} msgs
                </span>
              )}
            </div>

            {/* Secondary insight tabs */}
            <SecondaryTabs
              chatTarget={chatTarget}
              activeSecondary={secondaryTab}
              onTabChange={setSecondaryTab}
            />

            {false && secondaryTab !== null && secondaryTab !== 'personalities' && (
              <div
                className="flex items-center justify-between gap-3 px-3 py-2 shrink-0 bg-teal-50 dark:bg-teal-900/30 border-b border-teal-200 dark:border-teal-800"
                role="status"
              >
                <p className="text-xs text-app-text leading-snug min-w-0">
                  {secondaryTab ? SECONDARY_VIEW_BANNER[secondaryTab as Exclude<SecondaryTab, 'personalities'>] : ''}
                </p>
                <button
                  type="button"
                  onClick={() => setSecondaryTab(null)}
                  className="shrink-0 text-xs font-medium px-2.5 py-1 rounded-lg border border-teal-300 dark:border-teal-600 text-teal-800 dark:text-teal-100 hover:bg-teal-100/80 dark:hover:bg-teal-800/40 transition-colors"
                >
                  ← กลับแชทหลัก
                </button>
              </div>
            )}

            {/* Chat history OR device personalities grid */}
            {showPersonalities ? (
              <div className="flex-1 overflow-y-auto">
                <DevicePersonalitiesGrid devices={devices as Record<string, unknown>} loading={devicesLoading} />
              </div>
            ) : (
              <div className="flex-1 flex flex-col md:flex-row min-w-0 min-h-0">
                <div className={`flex-1 flex flex-col min-w-0 min-h-0 ${chatTarget === 'manager' ? 'border-b md:border-b-0 md:border-r border-app-border' : ''}`}>
                  <ChatHistory
                    history={history}
                    userMessages={userMessages}
                    agentColors={agentColors}
                    streaming={isStreaming}
                    chatTarget={chatTarget}
                    secondaryTab={secondaryTab === 'teamDiscussion' ? null : secondaryTab}
                    inputMode={inputMode}
                    unifiedManagerTranscript={unifiedManagerTranscript}
                  />
                </div>
                {chatTarget === 'manager' && (
                  <div className="flex flex-col min-w-0 min-h-0 bg-app-surface/50 shrink-0 max-md:flex-1 md:w-[180px] lg:w-[200px]">
                    <div className="px-3 py-1.5 border-b border-app-border bg-app-muted/30 shrink-0 flex items-center justify-between">
                      <span className="text-[11px] font-medium text-app-text-sec uppercase tracking-wide flex items-center gap-1.5">
                        <UserGroupIcon className="w-3.5 h-3.5" />
                        Team Discussion
                      </span>
                    </div>
                    <ChatHistory
                      history={history}
                      userMessages={userMessages}
                      agentColors={agentColors}
                      streaming={isStreaming}
                      chatTarget={chatTarget}
                      secondaryTab="teamDiscussion"
                      inputMode={inputMode}
                      unifiedManagerTranscript={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className="h-[280px] shrink-0 border-t border-app-border lg:h-auto lg:w-[38%] lg:min-w-[280px] lg:max-w-[440px] lg:border-t-0 lg:border-l flex flex-col min-h-0"
            aria-label="Station agent graph"
          >
            <div className="px-3 py-1.5 border-b border-app-border bg-app-muted shrink-0">
              <span className="text-[11px] font-medium text-app-text-sec uppercase tracking-wide">
                Station graph · กราฟสถานี
              </span>
            </div>
            <div className="flex-1 min-h-0 relative">
              <AgentGraphView
                stations={graphStations}
                stationsLoading={graphStationsLoading}
                stationsError={graphStationsError}
                selectedStationId={activeStationId}
                onStationSelect={setActiveStationId}
                nodeMessages={graphNodeMessages}
                activeSpeakers={graphActiveSpeakers}
                isProcessing={graphIsProcessing}
              />
            </div>
          </div>
        </div>
      </div>

      <ChatInput
        onSend={handleSend}
        disabled={
          isBusy ||
          isStopping ||
          secondaryTab !== null ||
          (!activeStationId && !isMaster && inputMode !== 'ask')
        }
        noControl={isMaster}
        controlDisabled={!canControl}
        inputMode={inputMode}
        onInputModeChange={setInputMode}
        placeholder={secondaryTab ? 'Switch back to main view to send a message' : chatPlaceholder}
        randomQuestionPool={randomPool}
        stopVisible={isBusy}
        onStop={handleStop}
        stopLoading={isStopping}
      />
    </div>
  );
}
